import { Input } from "@/components/ui/input";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "@/components/ui/combobox";
import { InputGroupAddon } from "@/components/ui/input-group";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Estimation } from "@/interfaces/estimation.interface";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface SelectFieldOptions {
  value: string;
  label: string;
  icon?: ReactNode;
  group?: string;
}

interface FieldDetailProps {
  label: string;
  name: keyof Estimation;
  value?: string;
  icon?: ReactNode;
  placeholder?: ReactNode | string;
  inputClassname?: string;
  variant?: "input" | "select";
  options?: SelectFieldOptions[];
  estimation: Estimation | undefined;
  updateEstimationDetails: (_estimation: Estimation, field: keyof Estimation, clientOnly?: boolean) => Promise<boolean>;
  disabled?: boolean;
  disabledTooltip?: string;
  /** Shown above the row (e.g. persistent validation hint). */
  announcement?: ReactNode;
  comboboxShowClear?: boolean;
  /** When true, blurring the combobox with an empty input commits a cleared selection (office-only). */
  allowEmptyCommitOnBlur?: boolean;
}

export const FieldDetail = ({
  estimation,
  updateEstimationDetails,
  label,
  inputClassname,
  icon,
  placeholder,
  variant = "input",
  options,
  value,
  name,
  disabled = false,
  disabledTooltip,
  announcement,
  comboboxShowClear = false,
  allowEmptyCommitOnBlur = false,
}: FieldDetailProps) => {
  const [edit, setEdit] = useState(false);

  const toggleEdit = useCallback(() => {
    setEdit((prev) => !prev);
  }, []);

  const onSelect = useCallback(
    (value: string) => {
      if (!estimation) return;
      const newEstimation = structuredClone(estimation);

      newEstimation[name] = value as never;
      updateEstimationDetails(newEstimation, name);
    },
    [estimation, name, updateEstimationDetails],
  );

  const commitValue = useCallback(
    (rawValue: string) => {
      if (!rawValue?.length) return;

      if (value === rawValue || !estimation) return;

      const newEstimation = structuredClone(estimation);

      newEstimation[name] = rawValue as never;
      updateEstimationDetails(newEstimation, name);
    },
    [estimation, name, updateEstimationDetails, value],
  );

  const onInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      commitValue(e.target.value);
      toggleEdit();
    },
    [commitValue, toggleEdit],
  );

  const selectedOption = useMemo(() => options?.find((opt) => opt.value === value) ?? null, [options, value]);

  const placeholderText = typeof placeholder === "string" ? placeholder : label;
  const displayIcon = selectedOption?.icon ?? icon;
  const anchorRef = useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = useState(selectedOption?.label ?? "");
  const committedLabelRef = useRef(selectedOption?.label ?? "");

  useEffect(() => {
    committedLabelRef.current = selectedOption?.label ?? "";
    setInputValue(selectedOption?.label ?? "");
  }, [selectedOption]);

  return (
    <div className="grid grid-cols-3">
      {announcement != null ? (
        <p className="col-span-3 mb-1.5 text-xs leading-snug text-amber-900 dark:text-amber-100">{announcement}</p>
      ) : null}
      <div className="flex w-[100px] flex-col justify-center text-xs font-medium whitespace-nowrap text-[#71717A]">
        <label>{label}</label>
      </div>
      {variant === "select" ? (
        <div className="col-span-2 w-[200px]" ref={anchorRef}>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild disabled={!disabled}>
                <div>
                  <Combobox
                    items={options || []}
                    itemToStringValue={(option) => option.label}
                    value={selectedOption}
                    onValueChange={(item) => {
                      if (disabled) return;
                      if (item == null) {
                        committedLabelRef.current = "";
                        setInputValue("");
                        onSelect("");
                        return;
                      }
                      committedLabelRef.current = item.label;
                      onSelect(item.value);
                    }}
                    inputValue={inputValue}
                    onInputValueChange={setInputValue}
                    onOpenChange={(open) => {
                      if (!open) {
                        setInputValue(committedLabelRef.current);
                      }
                    }}
                    disabled={disabled}
                  >
                    <ComboboxInput
                      placeholder={placeholderText}
                      className="h-7 text-sm"
                      disabled={disabled}
                      showClear={comboboxShowClear}
                      onFocus={(e) => {
                        const input = e.target as HTMLInputElement;
                        requestAnimationFrame(() => input.select());
                      }}
                      onBlur={(e) => {
                        if (!allowEmptyCommitOnBlur || disabled) return;
                        const raw = (e.target as HTMLInputElement).value.trim();
                        if (!raw) {
                          committedLabelRef.current = "";
                          setInputValue("");
                          onSelect("");
                          return;
                        }
                        setInputValue(committedLabelRef.current);
                      }}
                    >
                      {displayIcon && <InputGroupAddon align="inline-start">{displayIcon}</InputGroupAddon>}
                    </ComboboxInput>
                    <ComboboxContent anchor={anchorRef}>
                      <ComboboxEmpty>No items found.</ComboboxEmpty>
                      <ComboboxList>
                        {(option) => (
                          <ComboboxItem key={option.value === "" ? "__office_none__" : option.value} value={option}>
                            <span className="inline-flex items-center gap-2">
                              {option.icon}
                              {option.label}
                            </span>
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </div>
              </TooltipTrigger>
              {disabled && disabledTooltip && (
                <TooltipContent>
                  <p>{disabledTooltip}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      ) : (
        <div className="col-span-2">
          {edit ? (
            <div className="relative">
              {icon && <div className="absolute top-1/2 left-2 -translate-y-1/2 pt-1">{icon}</div>}
              <Input
                autoFocus
                onFocus={(e) => {
                  e.target.showPicker();
                }}
                type="text"
                defaultValue={value}
                onBlur={onInputBlur}
                placeholder={placeholder as string}
                className={`max-h-7 max-w-[200px] rounded-lg !border-gray-300 ${inputClassname}`}
              />
            </div>
          ) : (
            <div onClick={toggleEdit} className="flex cursor-pointer flex-row space-x-2">
              {icon && <div className="pt-1">{icon}</div>}
              <p className="text-sm text-[#9ca3af]">{value?.length ? value : placeholder}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
